//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';

import { ITwitterScheduleFailureContext } from '@apis/twitter-scheduled-tweets/twitterScheduledTweetsApiModel';
import { TPostConnection } from '@apis/universal-posts/connections/universalPostConnectionModel';
import { ITwitterUserSuggestion } from '@components/molecules/smart-input/smartInputModel';
import Cuid from '@models/Cuid';
import DraftId from '@models/DraftId';
import { IMediaMetadata } from '@models/INetworkItem';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';

import { RoutableStateName } from 'app/top-level-states';

/**
 * Information about the current post that cannot be altered
 * by any user input in the post-details form.
 */
export interface IStaticTwitterPostDetails {
  draftId?: DraftId;
  postId?: PostableId;
  postStatus?: PostableStatus;
  connections?: TPostConnection[];
}

/**
 * Information about the current post that can be directly
 * altered by user input in the post details form.
 */
export interface IComparableTwitterPostDetails {
  text: string;
  scheduledTime?: Date;
  cuids: Cuid[];
}

export interface ITwitterPostDetailsState extends IStaticTwitterPostDetails {
  currentPostDetails: IComparableTwitterPostDetails;
  errors: ITwitterPostDetailsErrors;
  flags: ITwitterPostDetailsFlags;
  initialPostDetails: IComparableTwitterPostDetails;
  linkPostPreviewData?: ITwitterLinkPostPreviewData;
  shortLinks: Record<string, string>;
  userSearches: Record<string, ITwitterUserSuggestion[]>;
  validationErrors: PojoSet<TwitterPostDetailsValidationError>;
  invalidCuids: PojoSet<Cuid>;
  schedulingEntryPoint: RoutableStateName | undefined;
}

interface ITwitterPostDetailsErrors {
  setInitialPostDetailsError?: string;
  fetchLinkMetadataError?: string;
  fetchSelectedMediaError?: string;
  scheduleFailureError?: string;
  scheduleFailureContext?: ITwitterScheduleFailureContext;
  draftFailureError?: string;
  shortLinkErrors: string[];
  userSearchErrors: string[];
}

interface ITwitterPostDetailsFlags {
  hasSetPostDetails: boolean;
  isFetchingLinkMetadata: boolean;
  isFetchingSelectedMedia: boolean;
  isSchedulingTweet: boolean;
  isDraftingTweet: boolean;
}

export interface ITwitterLinkPostPreviewData {
  linkMetadata?: ITwitterLinkPostMetadata;
  url?: string;
}

export interface ITwitterLinkPostMetadata {
  description?: string;
  destinationUrl?: string;
  imageUrl?: string;
  title?: string;
}

export enum TwitterPostDetailsValidationError {
  MediaTooLarge = 'MediaTooLarge',
  MediaTooSmall = 'MediaTooSmall',
  NoMediaOrText = 'NoMediaOrText',
  NoPostTime = 'NoPostTime',
  PostTimeInPast = 'PostTimeInPast',
  TextTooLong = 'TextTooLong',
}

export const fallbackComparableTwitterPostDetails = {
  text: '',
  cuids: [],
};

export const MAX_TWITTER_GALLERY_IMAGE_COUNT = 4;

// https://github.com/bvengineering/curalate-twitter-gateway-service/blob/master/server/src/main/scala/com/curalate/services/twitter_gateway/service/api/provider/ScheduledTweetVerifier.scala#L31
export const MAX_TWITTER_VIDEO_LENGTH = 140000; // 140s
export const MAX_TWEET_LENGTH = 280;
export const MIN_TWITTER_MEDIA_WIDTH_PX = 4;
export const MIN_TWITTER_MEDIA_HEIGHT_PX = 4;
export const MAX_TWITTER_MEDIA_WIDTH_PX = 8192;
export const MAX_TWITTER_MEDIA_HEIGHT_PX = 8192;

export const isTwitterMediaTooSmall = (mediaMetadata: IMediaMetadata) => {
  return (
    mediaMetadata.width < MIN_TWITTER_MEDIA_WIDTH_PX ||
    mediaMetadata.height < MIN_TWITTER_MEDIA_HEIGHT_PX
  );
};

export const isTwitterMediaTooLarge = (mediaMetadata: IMediaMetadata) => {
  return (
    mediaMetadata.width > MAX_TWITTER_MEDIA_WIDTH_PX ||
    mediaMetadata.height > MAX_TWITTER_MEDIA_HEIGHT_PX
  );
};

export const USER_SEARCH_COUNT = 20;
